import React from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'

import Image from 'components/image'
import { mobileAppDownloadPath, registryHomePath } from 'lib/urls'
import { InfoButton, CloseButton } from 'components/buttons'
import { Wave } from 'components/curves'
import { track } from 'lib/analytics'
import Link from 'components/link'
import CloseIcon from '@material-ui/icons/Close'

import css from './app-promo.scss'

import useStyles from './app-promo-wave.styles'

const trackModalExit = (trackAttributes, onClose) => {
  track.appDownloadInterstitialSkipped(trackAttributes)
  onClose()
}
const trackModalCtaClick = (trackAttributes, path) => {
  track.appDownloadInterstitialClicked(trackAttributes)
  window.location.href = mobileAppDownloadPath(path)
}

export const AppPromoWave = ({ trackAttributes, onClose }) => (
  <>
    <div className={classNames('text-center', css.promoWaveHeader)}>
      <div className={css.promoWaveHeaderEnd}>
        <CloseButton onClick={() => trackModalExit(trackAttributes, onClose)} />
      </div>
      <div className={`h1 ${css.promoWaveHeading}`}>
        Ready to add any item from any store?
      </div>
    </div>
    <div className={css.promoWaveComponentWrapper}>
      <Wave color="white" />
    </div>
    <div className={classNames('pbxxl text-center')}>
      <InfoButton
        pill
        className="mbl"
        onClick={() => trackModalCtaClick(trackAttributes)}
      >
        Get The Babylist App
      </InfoButton>
      <p>
        <Link
          onClick={() => trackModalExit(trackAttributes, onClose)}
          className={classNames('small', css.promoWaveLink)}
        >
          Continue browsing the mobile site
        </Link>
      </p>
    </div>
  </>
)

AppPromoWave.propTypes = {
  onClose: PropTypes.func,
  trackAttributes: PropTypes.object,
}

AppPromoWave.defaultProps = {
  onClose: () => {},
  trackAttributes: {},
}

export const AppPromoStartBuilding = ({ trackAttributes }) => (
  <>
    <div className={classNames('text-center', css.promoWaveHeader)}>
      <h1 className={css.promoWaveHeading}>
        Ready to start building your registry?
      </h1>
      <p className={classNames('tiny', css.promoWaveSubHeading)}>
        You'll need our 5-star rated app to add items from any online store
        (from your mobile device).
      </p>
    </div>
    <div className={css.promoWaveComponentWrapperSmall}>
      <Wave color="white" />
    </div>
    <div className={classNames('pbxxl text-center')}>
      <InfoButton
        pill
        className="mbl"
        onClick={() => trackModalCtaClick(trackAttributes)}
      >
        Get The Babylist App
      </InfoButton>
      <p className={classNames('tiny', css.promeWaveDisclaimerText)}>
        Without the babylist app, you can still add items from the Babylist
        Store and browse the mobile site.
      </p>
    </div>
  </>
)

export const AppPromoStartBuildingEx2Variant = ({ trackAttributes }) => {
  const user = typeof window === 'undefined' ? null : window.currentUser
  const firstName = user ? user.firstName : ''
  const classes = useStyles()
  return (
    <>
      <button
        type="button"
        className={classNames('close', classes.close)}
        onClick={() =>
          trackModalExit(
            trackAttributes,
            () => (window.location.href = registryHomePath)
          )
        }
      >
        <CloseIcon fontSize="large" />
      </button>

      <h1 className={classes.title}>
        <span className={classes.gradiantStrawberry}>W</span>
        <span className={classes.gradiantTurquoise}>E</span>
        <span className={classes.gradiantSunset}>L</span>
        <span className={classes.gradiantLemonlime}>C</span>
        <span className={classes.gradiantTurquoise}>O</span>
        <span className={classes.gradiantSunset}>M</span>
        <span className={classes.gradiantStrawberry}>E</span>
        <span className={classes.gradiantLemonlime}>!</span>
      </h1>
      <p className={classes.subtitle}>
        <strong>Your Babylist has been created.</strong>
        <div>{`Here are just a few ways we hope to help you on this journey${
          firstName.length ? `, ${firstName}` : ''
        }.`}</div>
      </p>
      <h2 className={classes.sectionHeader}>One Registry for All Stores</h2>
      <p className={classes.sectionDescription}>Add any item from anywhere</p>
      <Image
        className={classes.promoWaveImage}
        src="https://images.babylist.com/image/upload/f_auto,q_auto/v1679060395/appdownloadinterstitial1_yxvxjr.png"
      />
      <div className={classNames('pbxl text-center')}>
        <InfoButton
          pill
          className={classes.sectionCta}
          onClick={() =>
            trackModalCtaClick(
              { ...trackAttributes, eventCta: 'universal_registry' },
              '/add-items'
            )
          }
        >
          Try It Now
        </InfoButton>
      </div>
      <h2 className={classes.sectionHeader}>Guides & Sample Registries</h2>
      <button
        className={classes.promoImageButton}
        type="button"
        onClick={() =>
          trackModalCtaClick(
            { ...trackAttributes, eventCta: 'guides_creating_baby_registry' },
            '/hello-baby/how-to-create-a-baby-registry'
          )
        }
      >
        <Image
          className={classes.promoGuidesImage}
          src="https://images.babylist.com/image/upload/f_auto,q_auto/v1679604553/appdownloadinterstitial-guidesv2_pideum.png"
        />
      </button>
      <div className={classNames('pbxl text-center')}>
        <InfoButton
          pill
          className={classes.sectionCta}
          onClick={() =>
            trackModalCtaClick(
              { ...trackAttributes, eventCta: 'guides' },
              '/hello-baby'
            )
          }
        >
          Get More Expert Advice
        </InfoButton>
      </div>
      <h2 className={classes.sectionHeader}>Exclusive Products</h2>
      <p className={classes.sectionDescription}>
        Every baby is different so we offer try-it kits to discover what baby
        likes best.
      </p>
      <button
        className={classes.promoImageButton}
        type="button"
        onClick={() =>
          trackModalCtaClick(
            { ...trackAttributes, eventCta: 'store_bottle_box' },
            '/gp/babylist-bottle-box-5-bottles/26539/1261520'
          )
        }
      >
        <Image
          className={classes.promoWaveImage}
          src="https://images.babylist.com/image/upload/f_auto,q_auto/v1679061257/appdownloadinterstitial2_imvavb.png"
        />
      </button>
      <div className={classNames('pbxl text-center')}>
        <InfoButton
          pill
          className={classes.sectionCta}
          onClick={() =>
            trackModalCtaClick(
              { ...trackAttributes, eventCta: 'store_try_it_kits' },
              '/store/try-it-kits'
            )
          }
        >
          Browse Try-It Kits
        </InfoButton>
      </div>

      <div className={classNames(classes.promoWaveBackground)}>
        <Image
          className={classes.phoneImage}
          src="https://images.babylist.com/image/upload/f_auto,q_auto/v1679070298/appdownloadinterstitial3_bbowj3.png"
        />
        <div className={classes.sectionText}>
          <h3 className={classes.sectionTextTitle}>
            Ready to start building your registry?
          </h3>
          <p className={classes.sectionTextSubtitle}>
            You'll need our app to add items from any store.
          </p>
        </div>
        <Wave color="#f9f6f3" position="bottom" />
      </div>

      <div className={classNames('pbxxl text-center')}>
        <InfoButton
          pill
          className={classes.sectionCta}
          onClick={() =>
            trackModalCtaClick(
              { ...trackAttributes, eventCta: 'download_app' },
              registryHomePath
            )
          }
        >
          Get the App
        </InfoButton>
      </div>
    </>
  )
}

export const AppPromoNextStep = ({ onClose }) => (
  <>
    <div className={classNames('text-center', css.promoWaveHeader)}>
      <div className={css.promoWaveHeaderEnd}>
        <CloseButton onClick={onClose} />
      </div>
      <h1 className={css.promoWaveHeading}>Next Step: Get The App</h1>
      <p className={classNames('tiny', css.promoWaveSubHeading)}>
        You'll need our 5-star rated app to add items from any online store
        (from your mobile device).
      </p>
    </div>
    <div className={css.promoWaveComponentWrapperSmall}>
      <Wave color="white" />
    </div>
    <div className={classNames('pbxxl text-center')}>
      <InfoButton href={mobileAppDownloadPath()} className="mbl" pill>
        Get The Babylist App
      </InfoButton>
      <p className={classNames('tiny', css.promeWaveDisclaimerText)}>
        Nah, I want to{' '}
        <Link
          onClick={onClose}
          className={classNames('tiny', css.promoWaveLink)}
        >
          browse the mobile site
        </Link>{' '}
        and add items from Babylist Store only.
      </p>
    </div>
  </>
)

export const AppPromoCollaborator = ({ registrantNames }) => {
  return (
    <>
      <div className={classNames('text-center', css.promoWaveHeader)}>
        <h1 className={css.promoWaveHeading}>
          Get the app to help
          <br />
          {registrantNames}
          <br />
          build their registry.
        </h1>
        <p className={classNames('tiny', css.promoWaveSubHeading)}>
          On your phone? You'll need the app.
        </p>
      </div>
      <div className={css.promoWaveComponentWrapperSmall}>
        <Wave color="white" />
      </div>
      <div className={classNames('pbxxl text-center')}>
        <InfoButton href={mobileAppDownloadPath()} className="mbl" pill>
          Get The Babylist App
        </InfoButton>
      </div>
    </>
  )
}

AppPromoNextStep.propTypes = {
  onClose: PropTypes.func,
}

AppPromoNextStep.defaultProps = {
  onClose: () => {},
}
